import { apiClient } from './client';

/**
 * 팀 근태 API — backend `modules/hr/team-attendance.routes.ts` (근태 D4, 2026-08-24).
 *
 * ★★ **개인정보.** 휴가 *종류*는 담고(2026-09-06 사용자 결정 · 달력 판) 정정 *사유*는 담지 않는다 —
 *    「무슨 휴가인가」와 「왜 쉬는가」는 다른 말이다. 사유는 결재 문서 안에서만 본다.
 * ★ **옛 서버에는 이 라우트가 없다**(404). 부르는 쪽이 실패를 삼키고 화면을 접는다.
 */

export type MemberState = 'WORKING' | 'OFF' | 'LEAVE' | 'DEEMED' | 'ABSENT';

export interface TeamMemberAttendanceDto {
  /** 소속 (2026-08-29 · 판 F) — 전사에서는 이 둘이 «누구의 줄인가» 다. 옛 서버는 안 준다. */
  teamName?: string | null;
  divisionName?: string | null;
  userId: string;
  name: string;
  position: string | null;
  avatarUrl: string | null;
  isLeader: boolean;
  state: MemberState;
  /** `HH:MM`. 기록이 없으면 null. */
  checkIn: string | null;
  checkOut: string | null;
  /** 그날 기준 시각보다 늦었나 — 실측이 있는 날만 판정한다. */
  late: boolean;
  baseStart: string;
  baseEnd: string;
  scheduleName: string | null;
  workedMinutes: number;
  /** 간주 출근의 근거(출장·외근). */
  deemedKind: 'business_trip' | 'outside_work' | null;

  // ── 달력 판에서 늘어난 칸 (2026-09-06). **옛 서버는 안 준다** — 없으면 «모른다»로 둔다. ──
  /** 사번. 안 쓰는 회사도 있어 null 이 온다. */
  employeeNumber?: string | null;
  /**
   * 그날 휴가 «종류»(`annual`·`substitute` …).
   * ★★★ 종전에는 서버가 이 칸을 **일부러 뺐다**. 근태 현황이 달력이 되면서
   *    「누가 무슨 휴가로 비우나」가 화면의 물음이 되어 **사용자가 여는 쪽으로 정했다**
   *    (2026-09-06). **사유는 여전히 안 온다.**
   */
  leaveType?: string | null;
  /**
   * 그날 반차 · 반반차의 구간 `HH:mm` (S6 · 2026-09-16). 옛 기록 · 종일 · 옛 서버는 없거나 null.
   * 이름은 서버 계약 §9-6 확정(`leaveStartTime` · `leaveEndTime`).
   */
  leaveStartTime?: string | null;
  leaveEndTime?: string | null;
  /** 승인된 연장(분). */
  overtimeApprovedMinutes?: number;
  /** 신청은 했으나 결재가 대기 · 반려인 연장(분) — **결재자의 일**이다. */
  overtimePendingMinutes?: number;
  /** 소정을 넘겨 남았으나 **신청 자체가 없는** 시간(분) — **본인 · 팀장의 일**이다. */
  overtimeUnclaimedMinutes?: number;
  /** 쉬는 날인데 일한 시간(분) — 「휴일대체」로 바꿀 **대상**이다. */
  holidayWorkMinutes?: number;
  /**
   * 휴일대체가 **어디까지 왔나** (2026-09-06 · 판 C).
   *
   * ★★★ 종전 칸은 「쉬는 날인데 일한 시간」만 보여, 이미 냈는지 · 낼 수 있는지 · 바꿨는지가
   *    **다 같은 모양**이었다 — 담당자가 할 일을 알 수 없었다.
   * ★ `OFF` 는 **회사가 이 제도를 안 쓴다**는 뜻이다(지금 전부). 옛 서버는 이 칸을 안 준다.
   */
  substituteState?: 'OFF' | 'NONE' | 'ELIGIBLE' | 'PENDING' | 'APPROVED';
  /** 바뀔(또는 바뀐) 분 — **가산까지 넣은 값**이다. */
  substituteMinutes?: number;
  /** 승인된 조퇴가 있나. */
  earlyLeave?: boolean;
}

export interface TeamOptionDto {
  id: string;
  name: string;
  divisionName: string | null;
  /** 내가 이 팀의 팀장인가. 회사 관리자로 보는 팀은 false. */
  isLeader: boolean;
}

export interface TeamAttendanceDto {
  date: string;
  teams: TeamOptionDto[];
  /** 전사 범위면 고른 팀이 없다 — 화면이 «전사인가» 를 이것으로 안다. */
  team: TeamOptionDto | null;
  /** 무슨 범위로 받았나 (2026-08-29 · 판 F). 옛 서버는 안 준다. */
  scope?: 'team' | 'company';
  members: TeamMemberAttendanceDto[];
  summary: {
    working: number; off: number; onLeave: number; absent: number; total: number;
    creditedMinutes: number; scheduledMinutes: number;
    // ── 요약 묶음 (2026-09-06 · 정본 D9-3). 옛 서버는 안 준다. ──
    /**
     * 근무 중 — **찍고 아직 일하는 중**만. `working` 은 종전대로 간주(출장)까지 합친 값이다.
     * ★ `workingNow`·`off`·`onLeave`·`trip`·`absent` 다섯은 **겹치지 않고 합이 total** 이다.
     */
    workingNow?: number;
    /** 출장 · 외근(간주 출근). */
    trip?: number;
    late?: number;
    earlyLeave?: number;
    overtimeApproved?: { users: number; minutes: number };
    overtimePending?: { users: number; minutes: number };
    overtimeUnclaimed?: { users: number; minutes: number };
    holidayWork?: { users: number; minutes: number };
    /** 판 C — 휴일대체를 **아직 안 낸** 사람. 회사가 안 쓰면 늘 0 이다. */
    substituteEligible?: { users: number; minutes: number };
  };
  tzOffsetMinutes: number;
}

/**
 * 그날 근태. 팀장·회사 관리자만 — 그 밖에는 403.
 *
 * ★★ `scope: 'company'` 는 **한 번에 전사**를 준다(2026-08-29 · 판 F) — **전사를 보는 사람만**.
 *    부서·팀으로 좁히는 것은 **화면이 거른다**: 한 번 받아 거르면 왕복도 안 늘고
 *    위아래 숫자가 어긋날 일도 없다. 옛 서버는 이 값을 모르고 **종전대로 한 팀**을 준다.
 */
export async function getTeamAttendance(
  params?: { date?: string; teamId?: string; scope?: 'team' | 'company' },
): Promise<TeamAttendanceDto> {
  const { data } = await apiClient.get<TeamAttendanceDto>('/hr/attendance/team', { params });
  return data;
}

/**
 * 기간 근태 — **오늘 한 줄이 아니라 사람별 집계** (2026-08-30 · 판 F).
 *
 * ★★★ **「결근」 칸이 생겼다** (판 2-b · 2026-09-11). 종전에는 없었다 — 아래 문단은 «왜 없었나»의
 *    기록이다. 판 2(마이그레이션 189)가 근무제에 요일을 주면서 비로소 물을 수 있게 됐고,
 *    그래도 **못 세는 날은 0 이 아니라 «못 셈»** 으로 갈라 준다(`absence`).
 *
 * (종전 기록) 이 시스템은 어느 날이 근무일인지 모른다(주말 데이터 없음 ·
 *     근무제에 요일 칸 없음 · 회사마다 다르다). 기록 없는 날을 결근으로 세면 **한 달에 여드레쯤이
 *     거짓으로 붙는다.** 여기 담기는 것은 전부 **일어난 일**이다 — 화면도 그 사실을 적는다.
 */
export type AttendancePeriod = 'day' | 'week' | 'month';

export interface AttendanceRangeRow {
  userId: string;
  name: string;
  position: string | null;
  avatarUrl: string | null;
  isLeader: boolean;
  teamName: string | null;
  divisionName: string | null;
  /** 나온 날 — 실측 출근 **또는** 간주(출장·외근). 겹쳐 세지 않는다. */
  workedDays: number;
  lateDays: number;
  leaveDays: number;
  deemedDays: number;
  /** 퇴근을 안 찍은 날 — **오늘은 안 센다**(대개 아직 일하는 중이다). */
  openDays: number;
  creditedMinutes: number;
  /**
   * 결근과 **못 센 날** (판 2-b · 2026-09-11).
   *
   * ★ 옛 서버는 이 칸을 안 준다(`undefined`) — 그때 화면은 결근을 **아예 말하지 않는다**.
   *   「0」으로 그리면 「결근이 없다」로 읽힌다.
   */
  absence?: AbsenceTotalsDto;
}

/** 못 센 까닭. `absence.ts` 와 같은 말이다. */
export type UncountableReason =
  | 'NOT_EMPLOYED' | 'NO_HIRE_DATE' | 'NO_SCHEDULE' | 'NO_ROSTER' | 'NOT_YET';

export interface AbsenceTotalsDto {
  absentDays: number;
  uncountableDays: number;
  restDays: number;
  excusedDays: number;
  presentDays: number;
  uncountableBy: Record<UncountableReason, number>;
  /** 셀 수 있는 날이 하나라도 있었나. **`false` 면 「0」이 아니라 「집계할 수 없음」이다.** */
  countable: boolean;
}

/**
 * 못 센 까닭을 사람 말로 (판 2-b).
 *
 * ★★★ **담당자가 고칠 수 있는 것과 아닌 것을 가른다**(사용자 결정 2026-09-11).
 *    「입사일이 없습니다」는 누군가 넣으면 풀리는 것이고, 「아직 지나지 않은 날입니다」는
 *    기다리면 되는 것이다. 둘을 같은 목소리로 적으면 **무엇을 해야 하는지**가 안 보인다.
 */
export const UNCOUNTABLE_TEXT: Record<UncountableReason, { text: string; fixable: boolean }> = {
  NO_HIRE_DATE: { text: '입사일이 없습니다', fixable: true },
  NO_SCHEDULE: { text: '근무제가 정해지지 않았습니다', fixable: true },
  NO_ROSTER: { text: '교대 편성이 없습니다', fixable: true },
  NOT_EMPLOYED: { text: '재직 기간이 아닙니다', fixable: false },
  NOT_YET: { text: '아직 지나지 않은 날입니다', fixable: false },
};

/**
 * 못 센 까닭 중 **가장 많은 것** 하나. 없으면 `null`.
 *
 * ★ 화면 한 줄에 넷을 다 적으면 아무도 안 읽는다. **고칠 수 있는 것을 먼저** 고른다 —
 *   같은 수면 담당자가 손을 쓸 수 있는 쪽이 쓸모 있다.
 */
export function topUncountable(by: Record<UncountableReason, number>): UncountableReason | null {
  const entries = (Object.keys(UNCOUNTABLE_TEXT) as UncountableReason[])
    .filter((k) => (by[k] ?? 0) > 0);
  if (entries.length === 0) return null;
  entries.sort((a, b) => {
    const diff = (by[b] ?? 0) - (by[a] ?? 0);
    if (diff !== 0) return diff;
    return Number(UNCOUNTABLE_TEXT[b].fixable) - Number(UNCOUNTABLE_TEXT[a].fixable);
  });
  return entries[0] ?? null;
}

export interface AttendanceRangeDto {
  date: string;
  period: Exclude<AttendancePeriod, 'day'>;
  from: string;
  to: string;
  teams: TeamOptionDto[];
  team: TeamOptionDto | null;
  scope?: 'team' | 'company';
  members: AttendanceRangeRow[];
  summary: {
    total: number; workedDays: number; lateDays: number; leaveDays: number;
    openDays: number; creditedMinutes: number;
    /** 판 2-b — 옛 서버는 안 준다. */
    absentDays?: number;
    uncountableDays?: number;
  };
  tzOffsetMinutes: number;
}

/** 기간 근태. 권한은 오늘 것과 같다(팀장·회사 관리자). */
export async function getAttendanceRange(
  params: { period: Exclude<AttendancePeriod, 'day'>; date?: string; teamId?: string; scope?: 'team' | 'company' },
): Promise<AttendanceRangeDto> {
  const { data } = await apiClient.get<AttendanceRangeDto>('/hr/attendance/team', { params });
  return data;
}

/**
 * 근태 현황 내려받기 (2026-08-30 · 판 F).
 *
 * ★★ **파일은 서버가 만든다** — 개인정보가 밖으로 나가므로 **감사가 서버에 남아야** 하고,
 *    엑셀이 한글을 안 깨게 **BOM** 이 필요하며, 파일 이름도 한 곳에서 정해야 한다.
 *    화면은 **받아서 저장만** 한다.
 * ★ 이름은 서버가 `content-disposition` 에 실어 준다(한글이라 `filename*`). 못 읽으면 기본 이름.
 */
export async function downloadTeamAttendanceCsv(
  params?: { date?: string; teamId?: string; scope?: 'team' | 'company' },
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiClient.get('/hr/attendance/team', {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
  });
  const cd = String(res.headers['content-disposition'] ?? '');
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const filename = m ? decodeURIComponent(m[1]!) : '근태현황.csv';
  return { blob: res.data as Blob, filename };
}
